import { Component, Input, Output, EventEmitter, signal, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Feedback } from '../../../core/services/satisfaction.service';
import { SessionService } from '../../../core/session.service';
import { AuthService } from '../../../auth/auth.service';
import { TimezoneService } from '../../../core/services/timezone.service';
import { formatInTimeZone } from 'date-fns-tz';

@Component({
  selector: 'app-feedbacks-cards',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './feedbacks-cards.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./feedbacks-cards.component.scss']
})
export class FeedbacksCardsComponent {
  @Input() feedbacks: Feedback[] = [];
  @Input() showPendingOnly = false;
  @Input() showTreated = true;
  
  @Input() set highlightFeedbackId(id: string | null) {
    if (id) {
      this.highlightedFeedbackId.set(id);
      setTimeout(() => this.highlightedFeedbackId.set(null), 20000);
    }
  }
  
  private _activeFilter = signal<'none' | 'recontact' | 'actions-ongoing'>('none');
  @Input() set activeFilter(value: 'none' | 'recontact' | 'actions-ongoing') {
    this._activeFilter.set(value);
  }

  @Output() openReplyModal = new EventEmitter<Feedback>();
  @Output() openCallModal = new EventEmitter<Feedback>();
  // DEPRECATED (2026-09-08) : createAction/viewAction ne sont plus émis depuis le template (boutons
  // et badges action corrective retirés) -- voir la note en tête de satisfaction.component.ts.
  @Output() createAction = new EventEmitter<Feedback>();
  @Output() toggleTreatedView = new EventEmitter<void>();
  @Output() openBulkReply = new EventEmitter<Feedback[]>();
  @Output() archiveFeedback = new EventEmitter<string>();
  @Output() viewAction = new EventEmitter<Feedback>();

  private session = inject(SessionService);
  private auth = inject(AuthService);
  private translate = inject(TranslateService);
  private timezone = inject(TimezoneService);
  private readFeedbacks = signal<Set<string>>(new Set());

  showArchiveConfirm = false;
  feedbackToArchive: Feedback | null = null;

  showBulkSelection = false;
  selectedFeedbacks = new Set<string>();
  highlightedFeedbackId = signal<string | null>(null);

  constructor() {
    this.loadReadFeedbacks();
  }

  private getStorageKey(): string {
    const userId = this.session.selectedId();
    if (!userId) return '';
    
    // Hash simple pour anonymiser l'ID utilisateur
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      const char = userId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return `waitora_read_feedbacks_${Math.abs(hash)}`;
  }

  private loadReadFeedbacks() {
    try {
      const key = this.getStorageKey();
      if (!key) return;
      
      const stored = localStorage.getItem(key);
      if (stored) {
        const data = JSON.parse(stored);
        this.readFeedbacks.set(new Set(data.ids || []));
      }
    } catch (e) {
      console.error('Failed to load read feedbacks:', e);
    }
  }

  private markAsRead(feedbackId: string) {
    try {
      const key = this.getStorageKey();
      if (!key) return;
      
      const current = this.readFeedbacks();
      current.add(feedbackId);
      this.readFeedbacks.set(new Set(current));
      
      localStorage.setItem(key, JSON.stringify({
        ids: Array.from(current),
        lastUpdate: new Date().toISOString()
      }));
    } catch (e) {
      console.error('Failed to mark as read:', e);
    }
  }

  isRead(feedbackId: string): boolean {
    return this.readFeedbacks().has(feedbackId);
  }

  private applyActiveFilter(feedbacks: Feedback[]): Feedback[] {
    const filter = this._activeFilter();
    if (filter === 'recontact') {
      return feedbacks.filter(f => f.wants_reply && !f.replied_at);
    } else if (filter === 'actions-ongoing') {
      return feedbacks.filter(f => f.has_corrective_action && f.corrective_action_status === 'in_progress');
    }
    return feedbacks;
  }

  onCardClick(feedback: Feedback) {
    this.markAsRead(feedback.feedback_id);
  }

  toggleTreated() {
    this.toggleTreatedView.emit();
  }

  // DEPRECATED (2026-09-08) : ces 3 filtres excluaient auparavant les avis avec une action
  // corrective (`!f.has_corrective_action`), qui atterrissaient dans le 4e groupe "Actions"
  // (treatedFeedbacks, ci-dessous, retiré du template). Exclusion retirée : un avis ayant
  // historiquement une action corrective se classe désormais normalement selon son score/statut
  // de réponse, comme n'importe quel autre avis -- voir la note complète en tête de
  // satisfaction.component.ts.
  positiveFeedbacks = computed(() => {
    const filtered = this.applyActiveFilter(this.feedbacks);
    return filtered.filter(f => !f.wants_reply && !f.replied_at && (f.score || 0) >= 3);
  });

  negativeFeedbacks = computed(() => {
    const filtered = this.applyActiveFilter(this.feedbacks);
    return filtered.filter(f => !f.wants_reply && !f.replied_at && (f.score || 0) <= 2);
  });

  complaintFeedbacks = computed(() => {
    const filtered = this.applyActiveFilter(this.feedbacks);
    return filtered.filter(f => f.wants_reply);
  });

  // DEPRECATED (2026-09-08) : alimentait le 4e groupe "Actions" (bloc-traités), retiré du template
  // -- voir la note en tête de satisfaction.component.ts. Ne plus appeler ; conservé pour
  // faciliter un retour arrière.
  treatedFeedbacks = computed(() => {
    const filtered = this.applyActiveFilter(this.feedbacks);
    return filtered.filter(f => f.has_corrective_action);
  });

  getFeedbacksByStatus(status: string): Feedback[] {
    switch (status) {
      case 'positive': return this.positiveFeedbacks();
      case 'negative': return this.negativeFeedbacks();
      case 'complaint': return this.complaintFeedbacks();
      default: return [];
    }
  }

  getBucketLabel(bucket: number | null): string {
    const key = bucket === 1 ? 'short' : bucket === 2 ? 'moderate' : bucket === 3 ? 'long' : null;
    return key ? this.translate.instant('satisfaction.feedbacksCards.bucket.' + key) : '—';
  }

  getShortId(feedbackId: string): string {
    return feedbackId.substring(0, 8).toUpperCase();
  }

  getWaitTimeClass(reportedBucket: number | null): string {
    switch(reportedBucket) {
      case 1: return 'fluide';
      case 2: return 'moderee';
      case 3: return 'contrainte';
      default: return '';
    }
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '—';
    }
    const formatted = formatInTimeZone(date, this.timezone.getEstablishmentTimezone(), 'dd/MM/yyyy HH:mm');
    return `${formatted}${this.timezone.getTimezoneHint(date)}`;
  }

  toggleBulkReply(event: Event) {
    const target = event.target as HTMLInputElement;
    this.showBulkSelection = target?.checked || false;

    if (this.showBulkSelection) {
      // Sélectionner toutes les réclamations éligibles par défaut -- inclut désormais nativement
      // les avis autrefois classés dans la colonne "Actions" (has_corrective_action), retirée
      // (2026-09-08) : ils sont reclassés dans "complaint" comme n'importe quel avis avec
      // wants_reply=true, plus besoin d'un second appel dédié.
      this.selectedFeedbacks.clear();
      this.getFeedbacksByStatus('complaint')
        .filter(f => f.contact_email && !f.replied_at)
        .forEach(f => this.selectedFeedbacks.add(f.feedback_id));
    } else {
      this.selectedFeedbacks.clear();
    }
  }

  getSelectedComplaintsCount(): number {
    return this.feedbacks
      .filter(f => !f.replied_at && this.selectedFeedbacks.has(f.feedback_id))
      .length;
  }

  openBulkReplyModal() {
    const selectedFeedbacksList = this.feedbacks
      .filter(f => !f.replied_at && this.selectedFeedbacks.has(f.feedback_id));
    this.openBulkReply.emit(selectedFeedbacksList);
  }

  toggleFeedbackSelection(feedbackId: string, event: Event) {
    const target = event.target as HTMLInputElement;
    if (target?.checked) {
      this.selectedFeedbacks.add(feedbackId);
    } else {
      this.selectedFeedbacks.delete(feedbackId);
    }
  }

  confirmArchiveFeedback(feedback: Feedback) {
    this.feedbackToArchive = feedback;
    this.showArchiveConfirm = true;
    document.body.style.overflow = 'hidden';
  }

  executeArchive() {
    if (this.feedbackToArchive) {
      this.archiveFeedback.emit(this.feedbackToArchive.feedback_id);
      this.cancelArchive();
    }
  }

  cancelArchive() {
    this.showArchiveConfirm = false;
    this.feedbackToArchive = null;
    document.body.style.overflow = '';
  }

  getRatingLabel(rating: number | null): string {
    if (!rating || rating < 1 || rating > 5) return '—';
    return `${rating}/5`;
  }

  formatAgentName(fullName: string | null | undefined): string {
    if (!fullName) return this.translate.instant('satisfaction.feedbacksCards.unassigned');
    const parts = fullName.trim().split(' ');
    if (parts.length < 2) return fullName;
    const firstName = parts[0];
    const lastName = parts[parts.length - 1];
    return `${firstName.charAt(0)}.${lastName}`;
  }
}
